import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";

import type { Permission } from "@/roles/types";

import { currentUserQuery } from "../api/queries";

/**
 * This is a composable to conveniently check the information of the currently logged in user.
 * It also provides utilities to check the specific permissions they have.
 *
 * example usage:
 * ```
 * <script setup lang="ts">
 *    const { userHasPermission } = useCurrentUser();
 *    const userHasPermissionEditProducts: boolean = userHasPermission('Product.Write');
 *    // ...do something with this information
 * </script>
 * ```
 */
export const useCurrentUser = function () {
  const { data: user } = useQuery(currentUserQuery);

  const usersPermissions = computed(
    () => user.value?.permissions as Permission[],
  );

  const userHasPermission = (targetPermissions: Permission | Permission[]) => {
    if (Array.isArray(targetPermissions)) {
      return targetPermissions.every((target) =>
        usersPermissions.value?.includes(target),
      );
    }

    return usersPermissions.value?.includes(targetPermissions);
  };

  return {
    user,
    usersPermissions,
    userHasPermission,
  };
};
